import styled from '@emotion/styled';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { CONTENT_PADDING_TOP } from '@/components/shared/components/Layout/Layout.StyledComponents';

import {
    SECTION_HEADER_HEIGHT_VAR,
    SUBSECTION_HEADER_HEIGHT_VAR,
} from './ApplicantConditions.StyledComponents';

const Card = styled.div<{ isDeleted?: boolean; isFocused?: boolean; isDragOver?: boolean }>`
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: ${({ isDeleted }) => (isDeleted ? '#fafafa' : '#ffffff')};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    position: relative;
    overflow: clip;
    opacity: ${({ isDeleted }) => (isDeleted ? 0.6 : 1)};
    transition:
        border-color 200ms ease-in-out,
        box-shadow 200ms ease-in-out;
    ${({ isFocused }) =>
        isFocused ? 'box-shadow: 0 0 0 2px #0000ee, 0 0 0 6px rgba(0, 0, 238, 0.15);' : ''}
    ${({ isDragOver }) =>
        isDragOver
            ? 'border-color: #2563eb; box-shadow: 0 0 0 2px #2563eb, 0 0 0 6px rgba(37, 99, 235, 0.15);'
            : ''}
`;

const Header = styled.div`
    display: flex;
    align-items: stretch;
    position: relative;
`;

const DropOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 600;
    background: rgba(37, 99, 235, 0.06);
    border: 2px dashed #2563eb;
    border-radius: 8px;
    z-index: 1;
    pointer-events: none;
`;

const DROP_LABEL_TEXT = 'Drop files to attach to this Task/Condition';

const DropOverlayLabel = styled.span`
    position: sticky;
    top: calc(
        var(${SECTION_HEADER_HEIGHT_VAR}, 0px) + var(${SUBSECTION_HEADER_HEIGHT_VAR}, 0px) -
            ${CONTENT_PADDING_TOP} + 16px
    );
    bottom: calc(16px - ${CONTENT_PADDING_TOP});
`;

const NoteDropTargetWrapper = styled.div`
    position: relative;
`;

const NoteDropLabel = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 600;
    z-index: 1;
    pointer-events: none;
`;

const CardDragOverContext = createContext(false);

/**
 * Wraps the note box so a second drop label shows centered on it while files
 * are dragged over the card. Outside a card, or when not dragging, it adds nothing.
 */
export const NoteDropTarget = ({ children }: { children: React.ReactNode }) => {
    const isCardDragOver = useContext(CardDragOverContext);
    return (
        <NoteDropTargetWrapper>
            {children}
            {isCardDragOver ? <NoteDropLabel aria-hidden>{DROP_LABEL_TEXT}</NoteDropLabel> : null}
        </NoteDropTargetWrapper>
    );
};

/**
 * `padding-top` widens when the header renders a top-left action (gear menu)
 * so its 34x34 button doesn't overlap the "Show / More" label.
 * The selector targets ConditionActionButton via its data attribute.
 */
const ToggleZone = styled.button<{ isOpen?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 12px 4px 12px 12px;
    &:has(+ * [data-condition-action-slot]) {
        padding-top: 48px;
    }
    border: none;
    border-right: 1px solid #e2e8f0;
    background: #f8fafc;
    cursor: pointer;
    flex-shrink: 0;
    width: 64px;
    color: #3b82f6;
    transition: background 120ms ease-in-out;

    @media (hover: hover) {
        &:hover {
            background: #f1f5f9;
            color: #2563eb;
        }
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        background: #f1f5f9;
        outline: 2px solid #93c5fd;
        outline-offset: 2px;
    }
`;

const ToggleLabelGroup = styled.span`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    width: fit-content;
`;

const ToggleLabel = styled.span`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    color: inherit;
`;

const Caret = styled.span<{ up: boolean }>`
    display: block;
    margin-top: 2px;
    transition: transform 120ms ease-in-out;
    transform: rotate(${({ up }) => (up ? '180deg' : '0deg')});
    font-size: 10px;
    line-height: 1;
    text-align: center;
    color: inherit;
`;

const HeaderContent = styled.div`
    flex: 1;
    padding: 12px 20px;
    min-width: 0;
`;

const Content = styled.div<{ isDeleted?: boolean }>`
    padding: 16px 16px 20px 56px;
    background: ${({ isDeleted }) => (isDeleted ? '#fafafa' : '#ffffff')};
    border-top: 1px dashed #e2e8f0;
`;

interface ConditionCardProps {
    isDeleted?: boolean;
    isFocused?: boolean;
    isExpanded?: boolean;
    header: React.ReactNode | ((open: () => void, isOpen: boolean) => React.ReactNode);
    children: React.ReactNode;
    onFileDrop?: (files: File[]) => void;
    'data-condition-id'?: string;
}

// Hit testing treats the card as left <= x < right, so the far edges are outside.
// The card can extend past the window, so a pointer outside the window is outside too.
const isPointerOutside = (point: { clientX: number; clientY: number }, rect: DOMRect) =>
    point.clientX < Math.max(rect.left, 0) ||
    point.clientX >= Math.min(rect.right, window.innerWidth) ||
    point.clientY < Math.max(rect.top, 0) ||
    point.clientY >= Math.min(rect.bottom, window.innerHeight);

export const ConditionCard = ({
    isDeleted,
    isFocused,
    isExpanded = false,
    header,
    children,
    onFileDrop,
    'data-condition-id': conditionId,
}: ConditionCardProps) => {
    const [isOpen, setIsOpen] = useState(isExpanded);
    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounterRef = useRef(0);
    const toggle = () => setIsOpen((prev) => !prev);
    const open = () => setIsOpen(true);

    useEffect(() => {
        if (isFocused || isExpanded) setIsOpen(true);
    }, [isFocused, isExpanded]);

    const dropEnabled = Boolean(onFileDrop);
    const isFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes('Files');

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        if (!dropEnabled || !isFileDrag(e)) return;
        e.preventDefault();
        dragCounterRef.current += 1;
        setIsDragOver(true);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (!dropEnabled || !isFileDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!dropEnabled || !isFileDrag(e)) return;
        e.preventDefault();
        const nextTarget = e.relatedTarget;
        const leftCard =
            nextTarget instanceof Node
                ? !e.currentTarget.contains(nextTarget)
                : isPointerOutside(e, e.currentTarget.getBoundingClientRect());
        dragCounterRef.current = leftCard ? 0 : Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (!dropEnabled || !isFileDrag(e)) return;
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length > 0) onFileDrop?.(files);
    };

    return (
        <Card
            isDeleted={isDeleted}
            isFocused={isFocused}
            isDragOver={isDragOver}
            data-condition-id={conditionId}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid={conditionId ? `condition-drop-zone-${conditionId}` : undefined}
        >
            <Header>
                <ToggleZone
                    type="button"
                    isOpen={isOpen}
                    onClick={toggle}
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Show less' : 'Show more'}
                    data-testid={`condition-toggle-${conditionId}`}
                >
                    <ToggleLabelGroup>
                        <ToggleLabel data-testid={`condition-toggle-label-${conditionId}`}>
                            <span>Show</span>
                            <span>{isOpen ? 'Less' : 'More'}</span>
                        </ToggleLabel>
                        <Caret up={isOpen} aria-hidden>
                            ▾
                        </Caret>
                    </ToggleLabelGroup>
                </ToggleZone>
                <HeaderContent>
                    {typeof header === 'function' ? header(open, isOpen) : header}
                </HeaderContent>
            </Header>
            {isOpen ? (
                <CardDragOverContext.Provider value={isDragOver}>
                    <Content isDeleted={isDeleted}>{children}</Content>
                </CardDragOverContext.Provider>
            ) : null}
            {isDragOver ? (
                <DropOverlay>
                    <DropOverlayLabel>{DROP_LABEL_TEXT}</DropOverlayLabel>
                </DropOverlay>
            ) : null}
        </Card>
    );
};
